import { useTranslation } from 'react-i18next'

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { type AppLanguage, setPreferredLanguage } from '@/i18n'
import { cn } from '@/lib/utils'

export type { AppLanguage }

const LANGUAGES: { code: AppLanguage; label: string; flag: string }[] = [
  { code: 'he', label: 'עברית', flag: '🇮🇱' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
]

type LanguageSelectProps = {
  className?: string
  align?: 'start' | 'center' | 'end'
  onLanguageChange?: (lang: AppLanguage) => void
}

export function LanguageSelect({ className, align = 'end', onLanguageChange }: LanguageSelectProps) {
  const { i18n } = useTranslation()
  const currentLang: AppLanguage = i18n.language.startsWith('he') ? 'he' : 'en'
  const current = LANGUAGES.find(lang => lang.code === currentLang) ?? LANGUAGES[0]

  const setLanguage = (lang: AppLanguage) => {
    if (lang === currentLang) return
    setPreferredLanguage(lang)
    void i18n.changeLanguage(lang)
    onLanguageChange?.(lang)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          className={cn('rounded-md p-1.5 text-lg leading-none transition-colors hover:bg-gray-100', className)}
          title={current.label}
          aria-label={currentLang === 'he' ? 'בחירת שפה' : 'Select language'}
        >
          <span aria-hidden>{current.flag}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className='min-w-0 p-1'>
        {LANGUAGES.map(lang => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={cn('cursor-pointer justify-center px-2.5 py-1.5', currentLang === lang.code && 'bg-accent')}
            aria-label={lang.label}
          >
            <span className='text-lg leading-none' aria-hidden>
              {lang.flag}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
